import { fetchWithAuth, fetchJson } from './http';
import { AI_SERVICE_BASE_URL } from '../config/runtime';
import { DocumentItem, DocumentStatus } from '../types';

const RAG_READY_STATUS = 'RAG Chunks Processed';

interface SpringPage<T> {
  content?: T[];
  totalElements?: number;
  totalPages?: number;
}

function normalizeDoc(raw: any): DocumentItem {
  return {
    documentId: raw.documentId ?? raw.id ?? raw.document_id,
    name: raw.name ?? raw.title ?? raw.fileName ?? 'Untitled',
    documentTypeId: raw.documentTypeId ?? raw.document_type_id,
    documentTypeName: raw.documentTypeName ?? raw.document_type_name,
    endeavorId: raw.endeavorId ?? raw.endeavor_id,
    createdAt: raw.createdAt ?? raw.created_at,
    statuses: raw.statuses ?? [],
    fileSize: raw.fileSize ?? raw.file_size,
  };
}

// Generated audio (podcasts/audiobooks) is stored as documents with mime-style
// audio/* type names. Those belong in the Library, not the readable Documents
// list. Audio document-type IDs are dynamically assigned, so filter by name.
function isAudioDocument(doc: DocumentItem): boolean {
  return (doc.documentTypeName ?? '').toLowerCase().startsWith('audio/');
}

export interface ListDocumentsParams {
  page?: number;
  size?: number;
  sort?: string;
  name?: string;
  excludeDocumentTypeIds?: number[];
}

export async function listDocuments(userId: string, params: ListDocumentsParams = {}): Promise<DocumentItem[]> {
  const q = new URLSearchParams();
  q.set('page', String(params.page ?? 0));
  q.set('size', String(params.size ?? 100));
  q.set('sort', params.sort ?? 'createdAt,desc');
  if (params.name) q.set('name', params.name);
  (params.excludeDocumentTypeIds ?? [6]).forEach((id) => q.append('excludeDocumentTypeIds', String(id)));

  const res = await fetchWithAuth(`/api/users/${encodeURIComponent(userId)}/documents?${q.toString()}`);
  if (!res.ok) throw new Error(`Failed to load documents (${res.status})`);
  const data = (await res.json()) as SpringPage<any> | any[];
  const list = Array.isArray(data) ? data : data.content ?? [];
  return list.map(normalizeDoc).filter((doc) => !isAudioDocument(doc));
}

export async function getDocument(id: number): Promise<DocumentItem> {
  const raw = await fetchJson<any>(`/api/documents/${id}`);
  return normalizeDoc(raw);
}

export async function deleteDocument(id: number): Promise<void> {
  const res = await fetchWithAuth(`/api/documents/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete document (${res.status})`);
}

export interface UploadDocumentInput {
  file: File;
  userId: string;
  name?: string;
  documentTypeId?: number;
  description?: string;
  endeavorId?: number;
}

// Generic default document type (Article); the gateway requires a documentTypeId.
export const DEFAULT_DOCUMENT_TYPE_ID = 1;

// Uploads via the gateway storage endpoint (multipart, 15MB max, MD5-deduped).
export async function uploadDocument(input: UploadDocumentInput): Promise<{ documentId: number; isDuplicate?: boolean }> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('name', input.name ?? input.file.name);
  form.append('userId', input.userId);
  form.append('documentTypeId', String(input.documentTypeId ?? DEFAULT_DOCUMENT_TYPE_ID));
  if (input.description) form.append('description', input.description);
  if (input.endeavorId != null) form.append('endeavorId', String(input.endeavorId));

  const res = await fetchWithAuth('/api/storage/upload', { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Upload failed (${res.status})`);
  }
  const data = await res.json();
  const documentId = data.documentId ?? data.document?.documentId ?? data.document?.document_id ?? data.id;
  return { documentId, isDuplicate: data.isDuplicate };
}

// Kicks off RAG chunking so the document becomes search-ready. Blocks until done.
export async function processDocumentFull(id: number): Promise<void> {
  const res = await fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/documents/${id}/process/full`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(`Processing failed (${res.status})`);
}

export function isDocumentReadyForAudio(doc: DocumentItem): boolean {
  return (doc.statuses ?? []).some((s: DocumentStatus) => s.name === RAG_READY_STATUS);
}
